import type { DeckDetails } from "@amzn/ads-magneto-typescript-client";
import { Button, Tooltip } from "@amzn/storm-ui";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useStore } from "zustand";

import { ACCOUNT_AGNOSTIC_UNAVAILABLE } from "../../../config/constants";
import { LABELS } from "../../../constants/common";
import { Paths } from "../../../constants/paths";
import { TELEMETRY_ID } from "../../../constants/telemetry";
import { editContentStore } from "../../../store/editContent";
import { FeatureFlagsStore } from "../../../store/featureFlags";
import { discoveryFilterStore } from "../../../store/filters";
import { globalWorkbenchStore } from "../../../store/globalWorkbench";
import { navigationStore } from "../../../store/navigation";
import { salesforceStore } from "../../../store/salesforce";
import { workbenchStore } from "../../../store/workbench";
import { yourInsightFilterStore } from "../../../store/yourInsightsFilter";
import { TabToDefaultTypeMap } from "../../../types/filters/common";
import {
    YourInsightFilterContentType,
    YourInsightFilterContentSubType,
} from "../../../types/filters/yourInsights";
import { TopNavBarTabs } from "../../../types/navigation";
import {
    getAdvertiserId,
    isAccountAgnosticId,
} from "../../../utils/permission.utils";
import { logWithTakt } from "../../../utils/telemetry.utils";
import { getSearchParams } from "../../../utils/url.utils";
import {
    getWorkbenchLabel,
    WorkbenchUnit,
} from "../../common/getWorkbenchLabel";
import {
    DivFlexAlignCenter,
    DivFlexSpaceBetween,
} from "../../common/styled.components";
import { ContactUs } from "../../contactUs/ContactUs";
import { GlobalWorkbenchCartButton } from "../../remote/GlobalWorkbenchCartButton";
import { UploadPitchedContentModal } from "../insights/pitchedContents/UploadPitchedContentModal";

import { TabbedNav } from "./TabbedNav";

const StyledTopNavBarContainer = styled(DivFlexSpaceBetween)`
    border-bottom: 1px solid ${({ theme }) => theme.color.gray[200]};
`;

export interface TopNavBarProps {
    hasPlmAccess?: boolean;
}

export const TopNavBar: React.FunctionComponent<TopNavBarProps> = (
    props: TopNavBarProps,
) => {
    const { hasPlmAccess = false } = props;
    const advertiserId = getAdvertiserId();

    const [activeTab, setActiveTab, setIdentifier] = useStore(
        navigationStore,
        (state) => [state.activeTab, state.setActiveTab, state.setIdentifier],
    );
    const [pptxAssets, getTotalSlides] = useStore(workbenchStore, (state) => [
        state.pptxAssets,
        state.getTotalSlides,
    ]);

    const [setTypes] = useStore(discoveryFilterStore, (state) => [
        state.setTypes,
    ]);
    const [showEditModal] = useStore(editContentStore, (state) => [
        state.showEditModal,
    ]);

    const [sfName] = useStore(salesforceStore, (state) => [
        state.sfContext.name,
    ]);

    const [setYourInsightsTypes] = useStore(yourInsightFilterStore, (state) => [
        state.setTypes,
    ]);

    const hasWorkbenchAssets = pptxAssets.size > 0;

    const handleOnTabChange = (tab: TopNavBarTabs) => {
        if (!advertiserId && tab !== TopNavBarTabs.YOUR_INSIGHT_DECKS) {
            return;
        }

        const { contentType, contentSubType } = TabToDefaultTypeMap[tab];
        setTypes(contentType, contentSubType());
        setActiveTab(tab);
        logWithTakt(TELEMETRY_ID.TOP_NAV_BAR_SELECTION, {
            tab,
        });
    };

    const navigate = useNavigate();

    const handleCreateInsightClick = () => {
        navigate(`${Paths.WORKBENCH}${getSearchParams()}`);
    };
    const createInsightLabel: string = getWorkbenchLabel(
        LABELS.CREATE_INSIGHT_DECK,
        getTotalSlides(),
        WorkbenchUnit.SLIDE,
    );

    const shouldDisablePlmUpload = isAccountAgnosticId();

    return (
        <StyledTopNavBarContainer>
            <TabbedNav
                activeTab={activeTab}
                handleOnTabChange={handleOnTabChange}
            />
            <DivFlexAlignCenter>
                <ContactUs />
                {FeatureFlagsStore.getState().globalWorkbench ? (
                    <GlobalWorkbenchCartButton
                        workbenchScope={advertiserId}
                        overrideInsightsHubRedirect={(savedDeckId?: string) => {
                            setActiveTab(TopNavBarTabs.YOUR_INSIGHT_DECKS);
                            setYourInsightsTypes(
                                YourInsightFilterContentType.INSIGHTS,
                                YourInsightFilterContentSubType.WORKBENCH_INSIGHTS,
                            );
                            setIdentifier(savedDeckId);
                        }}
                        title={`${sfName} Insights Workbench`}
                        onCartDataLoaded={(cartData: DeckDetails) => {
                            globalWorkbenchStore
                                .getState()
                                .setCartData(cartData);
                        }}
                    />
                ) : (
                    <Button
                        variant={hasWorkbenchAssets ? "primary" : "default"}
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                        disabled={!hasWorkbenchAssets}
                        onClick={() => handleCreateInsightClick()}
                        data-testid="top-nav-bar-create-insight-deck"
                    >
                        {createInsightLabel}
                    </Button>
                )}
                {activeTab === TopNavBarTabs.YOUR_INSIGHT_DECKS &&
                    hasPlmAccess && (
                        <Tooltip
                            nonInteractive={!shouldDisablePlmUpload}
                            inert={!shouldDisablePlmUpload}
                            message={ACCOUNT_AGNOSTIC_UNAVAILABLE}
                            trigger={
                                <span>
                                    <Button
                                        onClick={() => {
                                            showEditModal();
                                        }}
                                        data-testid="top-nav-bar-upload-insight-deck"
                                        disabled={shouldDisablePlmUpload}
                                    >
                                        Upload insight deck
                                    </Button>
                                </span>
                            }
                        />
                    )}
            </DivFlexAlignCenter>
            <UploadPitchedContentModal />
        </StyledTopNavBarContainer>
    );
};
